<template>
  <v-app>
    <KHaloBackground v-if="checkDisplay()" class="app-halo" />
    <Navigation v-if="checkDisplay()" />
    <KToastStack :toasts="toasts" @close="dismissToast" @action="onToastAction" />
    <v-main :class="{ 'v-main--bare': !checkDisplay() }">
      <router-view v-slot="{ Component }">
        <transition name="fade" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
    </v-main>
    <PageFooter v-if="checkDisplay()" />
  </v-app>
</template>

<script>
import jsonPackage from "@/../package.json";
import Navigation from "./components/Navigation";
import PageFooter from "./components/PageFooter";
import KHaloBackground from "@/components/ui/KHaloBackground";
import KToastStack from "@/components/ui/KToastStack";

import { mapState, mapActions } from "pinia";
import { useUiStore, setSnackbar } from "@/stores/ui";
import { useUserStore } from "@/stores/user";
import { useCompanyStore } from "@/stores/company";
import { capture } from "@/posthog";
import { applyPwaUpdate } from "@/utils/pwaUpdate";

export default {
  name: "App",

  components: {
    Navigation,
    PageFooter,
    KHaloBackground,
    KToastStack,
  },

  setup() {
    const userStore = useUserStore();
    const companyStore = useCompanyStore();
    return { userStore, companyStore };
  },

  head() {
    return {
      title: this.$route.meta.title,
    };
  },

  data() {
    return {
      authReady: false,
    };
  },

  computed: {
    ...mapState(useUiStore, ["toasts"]),
  },
  watch: {
    $route: function() {
      this.checkVersion();
      this.checkModuleAccess();
    },
    "userStore.user": {
      handler(user) {
        if (user && user.company)
          this.companyStore.initCompanyListener(user.company);
        else
          this.companyStore.stopCompanyListener();

        this.applyUserTheme(user);
      },
      immediate: true,
    },
    "userStore.themeWrite": function() {
      this.applyUserTheme(this.userStore.user);
    },
    "companyStore.modules": {
      handler() {
        if (!this.companyStore.loading)
          this.checkModuleAccess();
      },
      deep: true,
    },
  },
  methods: {
    ...mapActions(useUiStore, ["dismissToast"]),
    onToastAction(toast) {
      if (toast.action === "pwa-reload")
        applyPwaUpdate();

      this.dismissToast(toast.id);
    },
    applyUserTheme(user) {
      const savedTheme = user && user.settings && user.settings.appTheme;
      const theme = this.userStore.themeWrite || savedTheme;

      if (theme === "dark" || theme === "light")
        this.$vuetify.theme.change(theme);
      else if (!user)
        this.$vuetify.theme.change("dark");
    },
    checkDisplay() {
      if (!this.authReady) return false;

      return this.$route.name !== "login"
        && this.$route.name !== "Login"
        && this.$route.name !== "register"
        && this.$route.name !== "Register"
        && this.$route.name !== "LoginApplication"
        && !this.$route.meta.hideShell;
    },
    checkVersion() {
      if (localStorage.getItem("katana:appVer") !== jsonPackage.version) {
        localStorage.setItem("katana:lastCheck", JSON.stringify({
          timestamp: new Date(),
          verFrom: localStorage.getItem("katana:appVer"),
        }));
        globalThis.location.reload();
      }

      localStorage.setItem("katana:appVer", jsonPackage.version);
    },

    checkModuleAccess() {
      if (!this.authReady) return;
      if (this.companyStore.loading) return;
      if (!this.userStore.user || !this.userStore.user.company) return;
      if (this.$route.meta.module === undefined) return;

      const moduleId = this.$route.meta.module;
      const access = Array.isArray(this.companyStore.modules) && this.companyStore.modules.some(m => m.id === moduleId);

      if (!access) {
        /* eslint-disable camelcase */
        capture("module_access_denied", {
          module_id: moduleId,
          route_name: this.$route.name,
        });
        /* eslint-enable camelcase */
        this.$router.replace({
          path: "/",
        });

        setSnackbar({
          text: "Vous n'avez pas accès à ce contenu !",
          color: "error",
        });
      }
    },
  },
  created() {
    this.checkVersion();

    this.$firebase.auth().onAuthStateChanged(async(user) => {
      try {
        if (user)
          await this.userStore.initUserListener(user.uid);
        else {
          this.userStore.stopUserListener();
          this.companyStore.stopCompanyListener();
        }
      } finally {
        this.authReady = true;
      }
    });
  },
};
</script>

<style lang="scss">
  .glassmorphism {
    background: none!important;
    background-color: rgba(255, 255, 255, 0)!important;
    backdrop-filter: blur(8px)!important;
  }

  .app-halo {
    position: fixed;
    z-index: 0;
  }

  .v-main {
    position: relative;
    z-index: 1;
  }

  ::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }

  ::-webkit-scrollbar-track {
    background: transparent;
  }

  ::-webkit-scrollbar-thumb {
    background-color: #b9bec7;
    border-radius: 12px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background-color: #4879a7;
  }

  ::-webkit-scrollbar-thumb:active {
    background-color: #2f3742;
  }
</style>
